/**
 * The one `BroadcastChannel` factory (FE Arch 1.0 Step 2, #1166).
 *
 * Cross-tab messages (annotation deltas, session identity, viewer preferences)
 * each keep their own channel name and message format — so tabs loaded before
 * and after a deploy still understand each other — but open it here, with the
 * same guards and the same test seam:
 *
 * - The bare global is read, not `window.BroadcastChannel`: under jsdom the
 *   window has none even though the bare name resolves (to Node's).
 * - Never open a channel at module load: Node's channel keeps the event loop
 *   alive and would hang Mocha. Callers open lazily; `global_setup.ts` installs a
 *   factory that returns `null`, and suites that assert on messages install a fake.
 */

/** The slice of `BroadcastChannel` the app uses. `onMessage` receives the DATA, not the event. */
export interface BroadcastPort {
    postMessage(data: unknown): void;
    close(): void;
    onMessage: ((data: unknown) => void) | null;
}

export type BroadcastFactory = (name: string) => BroadcastPort | null;

function openRealPort(name: string): BroadcastPort | null {
    if (typeof BroadcastChannel === 'undefined') return null;
    let raw: BroadcastChannel;
    try {
        raw = new BroadcastChannel(name);
    } catch {
        return null;
    }
    const port: BroadcastPort = {
        onMessage: null,
        postMessage: (data: unknown) => raw.postMessage(data),
        close: () => raw.close(),
    };
    raw.onmessage = (event: MessageEvent) => port.onMessage?.(event.data);
    return port;
}

let factory: BroadcastFactory = openRealPort;

/** A port on channel `name`, or `null` when the browser has no `BroadcastChannel`. */
export function openBroadcastPort(name: string): BroadcastPort | null {
    return factory(name);
}

/** Replace the factory. Test seam only — never call from app code. */
export function setBroadcastFactoryForTests(next: BroadcastFactory): void {
    factory = next;
}

/** Back to the real factory. Test seam only. */
export function resetBroadcastForTests(): void {
    factory = openRealPort;
}
